import { Box, Stack, Typography, LinearProgress } from '@mui/material';
import { iriToId, serverImageUrl } from '../../../util';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';

function SlideNavigation() {
  return (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: '12px',
        py: '16px',
        bottom: 0,
      }}
    >
      <Box
        sx={{
          background: '#234355',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: '4px',
          color: '#fff',
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 12 }} />
      </Box>
      <Box
        sx={{
          borderRadius: 10,
          px: '6px',
          py: '4px',
          background: '#234355',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 8,
          fontWeight: 500,
          gap: '2px',
        }}
      >
        <BookmarkBorderIcon sx={{ fontSize: 12 }} /> Save
      </Box>
      <Box
        sx={{
          background: '#234355',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: '4px',
          color: '#fff',
        }}
      >
        <ArrowForwardIcon sx={{ fontSize: 12 }} />
      </Box>
    </Box>
  );
}

function SlideProgressBar() {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 16,
        left: 12,
        width: 150,
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <LinearProgress
        variant="determinate"
        value={50}
        sx={{
          height: 3,
          borderRadius: 1.5,
          backgroundColor: '#fff',
          flex: 1,
          mr: 1,
          '& .MuiLinearProgress-bar': {
            backgroundColor: '#932018',
            borderRadius: 1.5,
          },
        }}
      />
    </Box>
  );
}

function SlideCloseButton() {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        zIndex: 3,
        background: 'white',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: '4px',
        color: 'black',
      }}
    >
      <CloseIcon sx={{ fontSize: 12 }} />
    </Box>
  );
}

function QuestionCard({ question }: { question: string }) {
  return (
    <Box
      sx={{
        backgroundColor: '#F8F5F0',
        borderRadius: '12px',
        p: '10px',
        boxShadow: '0 3px 12px rgba(0, 0, 0, 0.05)',
        border: '1px solid #D5D1C9',
        gap: '8px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        sx={{
          fontSize: '9px',
          lineHeight: '12px',
          fontWeight: 600,
          fontFamily: 'Figtree, sans-serif',
        }}
      >
        {question}
      </Typography>
      <Box
        sx={{
          borderRadius: '8px',
          px: '6px',
          py: '4px',
          border: '1px solid #D5D1C9',
        }}
      >
        <Typography
          sx={{
            fontSize: '8px',
            lineHeight: '12px',
            color: '#636363',
            fontFamily: 'Figtree, sans-serif',
          }}
        >
          Type something...
        </Typography>
      </Box>
    </Box>
  );
}

interface SlidePreviewProps {
  title?: string | null;
  description?: string | null;
  image?: string | null;
  backgroundImage?: string | null;
  backgroundVideo?: string | null;
  category?: string | null;
  type: 'text' | 'text-image' | 'image' | 'fact';
  question?: string | null;
}

function EmptySlide() {
  return (
    <Box
      sx={{
        width: 195,
        height: 400,
        background: '#ECE7DF',
        borderTopLeftRadius: 15,
        borderTopRightRadius: 15,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <SlideProgressBar />
      <SlideCloseButton />

      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          height: '100%',
          width: '100%',
          p: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            color: '#000',
            textAlign: 'center',
            fontFamily: 'Figtree, sans-serif',
          }}
        >
          Please add content
          <br />
          to see a preview
        </Typography>
      </Stack>

      <SlideNavigation />
    </Box>
  );
}

interface TextContentProps {
  title?: string | null;
  description?: string | null;
  category?: string | null;
}

function TextContent({ title, description, category }: TextContentProps) {
  return (
    <Stack
      sx={{
        height: '100%',
        width: '100%',
        p: 2,
        gap: 1,
        pt: 3,
      }}
    >
      {category && (
        <Box
          sx={{
            borderRadius: '4px',
            padding: '2px 4px',
            fontSize: '8px',
            fontWeight: 500,
            color: 'white',
            bgcolor: '#ECE7DF',
            mb: 1,
          }}
        >
          {category}
        </Box>
      )}
      {title && (
        <Typography
          sx={{ fontSize: '16px', lineHeight: '24px', fontWeight: 600 }}
        >
          {title}
        </Typography>
      )}
      {description && (
        <MarkdownDescription
          text={description}
          sx={{
            fontSize: '8px',
            fontWeight: 500,
            whiteSpace: 'pre-wrap',
            fontFamily: 'Figtree, sans-serif',
          }}
        />
      )}
    </Stack>
  );
}

interface VideoSlideProps {
  title?: string | null;
  backgroundVideo: string;
}

function VideoSlide({ title, backgroundVideo }: VideoSlideProps) {
  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {title && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 1,
            borderRadius: '4px',
            padding: '2px 4px',
            fontSize: '8px',
            fontWeight: 500,
            color: 'black',
            bgcolor: 'white',
          }}
        >
          {title}
        </Box>
      )}
      <video
        src={backgroundVideo}
        muted
        disablePictureInPicture
        controls
        style={{
          width: '100%',
          height: '100%',
          borderRadius: 8,
        }}
      />
    </Box>
  );
}

interface ImageSlideProps {
  title?: string | null;
  description?: string | null;
  image: string;
}

function ImageSlide({ title, description, image }: ImageSlideProps) {
  const getImageUrl = (imageId: string) => {
    try {
      return serverImageUrl({ id: iriToId(imageId), w: 202 });
    } catch (error) {
      console.warn('Failed to generate image URL:', error);
      return null;
    }
  };

  const imageUrl = getImageUrl(image);

  return (
    <Stack
      sx={{
        height: '100%',
        width: '100%',
        p: 2,
        gap: 1,
        pt: 5,
        position: 'relative',
      }}
    >
      {title && (
        <Typography
          sx={{ fontSize: '16px', lineHeight: '24px', fontWeight: 600 }}
        >
          {title}
        </Typography>
      )}
      {description && (
        <MarkdownDescription
          text={description}
          sx={{
            fontSize: '8px',
            fontWeight: 500,
            whiteSpace: 'pre-wrap',
            fontFamily: 'Figtree, sans-serif',
          }}
        />
      )}
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          bottom: 32,
          width: 165,
          height: 124,
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
        }}
      />
    </Stack>
  );
}

export default function SlidePreview({
  title,
  description,
  image,
  backgroundImage,
  backgroundVideo,
  category,
  type,
  question,
}: SlidePreviewProps) {
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @font-face {
        font-family: 'Beaufort';
        src: url('/src/assets/fonts/Beaufort-Regular.ttf') format('truetype');
        font-weight: 400;
        font-style: normal;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const hasContent =
    title ||
    description ||
    image ||
    backgroundVideo ||
    backgroundImage ||
    question;

  const getImageUrl = (imageId: string | null | undefined) => {
    if (!imageId) return null;
    try {
      return serverImageUrl({ id: iriToId(imageId), w: 202 });
    } catch (error) {
      console.warn('Failed to generate image URL:', error);
      return null;
    }
  };

  const imageUrl = getImageUrl(image);
  const backgroundImageUrl = getImageUrl(backgroundImage);

  const backgroundStyle = backgroundImageUrl
    ? `url(${backgroundImageUrl})`
    : 'linear-gradient(to bottom, #ECE7DF, #ECE7DF)';

  if (type === 'text-image') {
    if (!title && !description && !question) {
      return <EmptySlide />;
    }
    return (
      <Box
        sx={{
          width: 195,
          height: 400,
          background: '#ECE7DF',
          borderTopLeftRadius: 15,
          borderTopRightRadius: 15,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <SlideProgressBar />
        <SlideCloseButton />

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 60,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              left: 0,
              marginRight: '48px',
              height: 192,
              backgroundColor: '#D6CEC2',
              borderRadius: '0 0 10px 0',
              paddingRight: '12px',
              paddingBottom: '12px',
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                backgroundImage: `url(${imageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          </Box>

          <Stack sx={{ p: 2, gap: 1, pb: 4 }}>
            {title && (
              <Typography
                sx={{
                  fontSize: '15px',
                  lineHeight: '20px',
                  fontWeight: 400,
                  fontFamily: "'Beaufort', sans-serif",
                }}
              >
                {title}
              </Typography>
            )}
            {description && (
              <MarkdownDescription
                text={description}
                sx={{
                  fontSize: '8px',
                  fontWeight: 500,
                  fontFamily: 'Figtree, sans-serif',
                }}
              />
            )}
            {question && <QuestionCard question={question} />}
          </Stack>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            bottom: 60,
            left: 0,
            right: 0,
            height: 50,
            background:
              'linear-gradient(to bottom, rgba(236, 231, 223, 0) 0%, rgba(236, 231, 223, 0.78) 100%)',
            pointerEvents: 'none',
          }}
        />

        <SlideNavigation />
      </Box>
    );
  }

  if (type === 'image') {
    if (!title && !description && !imageUrl) {
      return <EmptySlide />;
    }
    return (
      <Box
        sx={{
          width: 195,
          height: 400,
          borderTopLeftRadius: 15,
          borderTopRightRadius: 15,
          position: 'relative',
          overflow: 'hidden',
          backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 120,
            background:
              'linear-gradient(to top, #ECE7DF 0%, #ECE7DF 60px, transparent 100%)',
          }}
        />

        <SlideProgressBar />
        <SlideCloseButton />

        {(title || description) && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 60,
              left: 12,
              right: 12,
              zIndex: 1,
            }}
          >
            {title && (
              <Typography
                sx={{
                  fontSize: '16px',
                  lineHeight: '22px',
                  fontWeight: 600,
                  color: 'white',
                  mb: 0.5,
                }}
              >
                {title}
              </Typography>
            )}
            {description && (
              <Typography
                sx={{
                  fontSize: '10px',
                  lineHeight: '14px',
                  fontWeight: 400,
                  color: 'white',
                  opacity: 0.9,
                  fontFamily: 'Figtree, sans-serif',
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
        )}

        <SlideNavigation />
      </Box>
    );
  }

  if (type === 'fact') {
    if (!description) {
      return <EmptySlide />;
    }
    return (
      <Box
        sx={{
          width: 195,
          height: 400,
          borderTopLeftRadius: 15,
          borderTopRightRadius: 15,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#ECE7DF',
        }}
      >
        {backgroundImageUrl && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${backgroundImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.6,
            }}
          />
        )}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 120,
            background:
              'linear-gradient(to top, #ECE7DF 0%, #ECE7DF 60px, transparent 100%)',
          }}
        />

        <SlideProgressBar />
        <SlideCloseButton />

        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'calc(100% - 24px)',
            backgroundColor: '#F6F3F1',
            borderRadius: '10px',
            padding: '10px',
          }}
        >
          {description && (
            <Typography
              sx={{
                fontFamily: "'Beaufort', sans-serif",
                fontSize: '11px',
                lineHeight: '13px',
              }}
            >
              {description}
            </Typography>
          )}
        </Box>

        <SlideNavigation />
      </Box>
    );
  }

  if (type === 'text') {
    if (!title && !description && !question) {
      return <EmptySlide />;
    }
    return (
      <Box
        sx={{
          width: 195,
          height: 400,
          background: '#ECE7DF',
          borderTopLeftRadius: 15,
          borderTopRightRadius: 15,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <SlideProgressBar />
        <SlideCloseButton />

        <Box
          sx={{
            position: 'absolute',
            top: 40,
            left: 0,
            right: 0,
            bottom: 60,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <Stack sx={{ p: 2, gap: 1, pb: 4 }}>
            {title && (
              <Typography
                sx={{
                  fontSize: '15px',
                  lineHeight: '20px',
                  fontWeight: 400,
                  fontFamily: "'Beaufort', sans-serif",
                }}
              >
                {title}
              </Typography>
            )}
            {description && (
              <MarkdownDescription
                text={description}
                sx={{
                  fontSize: '8px',
                  fontWeight: 500,
                  fontFamily: 'Figtree, sans-serif',
                }}
              />
            )}
            {question && <QuestionCard question={question} />}
          </Stack>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            bottom: 60,
            left: 0,
            right: 0,
            height: 50,
            background:
              'linear-gradient(to bottom, rgba(236, 231, 223, 0) 0%, rgba(236, 231, 223, 0.78) 100%)',
            pointerEvents: 'none',
          }}
        />

        <SlideNavigation />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 195,
        height: 400,
        background: backgroundStyle,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderTopLeftRadius: 15,
        borderTopRightRadius: 15,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <SlideProgressBar />
      <SlideCloseButton />
      <Box sx={{ width: '100%', height: '100%' }}>
        {!hasContent ? (
          <EmptySlide />
        ) : backgroundVideo ? (
          <VideoSlide title={title} backgroundVideo={backgroundVideo} />
        ) : type === 'image' && (title || description) ? (
          <ImageSlide
            title={title}
            description={description}
            image={image || ''}
          />
        ) : (
          <TextContent
            title={title}
            description={description}
            category={category}
          />
        )}
      </Box>
      <SlideNavigation />
    </Box>
  );
}

function MarkdownDescription({ text, sx }: { text: string | null; sx?: any }) {
  if (!text) return null;

  return (
    <ReactMarkdown
      components={{
        ul: ({ node, ...props }) => (
          <ul style={{ margin: 0, paddingLeft: 8 }} {...props} />
        ),
        li: ({ node, ...props }) => (
          <li
            style={{
              ...sx,
              fontFamily: sx?.fontFamily || 'Figtree, sans-serif',
            }}
            {...props}
          />
        ),
        p: ({ node, ...props }) => (
          <Typography
            sx={{ ...sx, fontFamily: sx?.fontFamily || 'Figtree, sans-serif' }}
            {...props}
          />
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
}
